import { Badge } from "react-bootstrap";

import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import TrellisLink from "../../components/core/basic/TrellisLink";
import { snakeCaseToSentenceCase } from "../../utils/formatText";
import * as Flags from "country-flag-icons/react/3x2";
import { TbBrandWalmart } from "react-icons/tb";
import { FaAmazon } from "react-icons/fa";
import { FaShopify } from "react-icons/fa6";
import { css } from "../../../styled-system/css";
import type { OrgQueryV4Row } from "../perf_reports/api/organization/v4OrgReportByOrg";
import { type Organization } from "../../zodios/common/orgSchema";
import { useSetOrgId } from "../admin/api/useSetCurrentOrg";
import { OverflowTags } from "../../components/core/blocks/OverflowTags";

const renderChannelIcon = (channel: string) => {
  switch (channel) {
    case "WALMART":
      return <TbBrandWalmart />;
    case "SHOPIFY":
      return <FaShopify />;
    case "AMAZON":
    default:
      return <FaAmazon />;
  }
};

type OrgCellData = OrgQueryV4Row | Organization;

export const OrgCell = ({
  orgData,
  marketPlace,
  changeOrgOnClick = true,
}: {
  orgData: OrgCellData;
  marketPlace?: string;
  changeOrgOnClick?: boolean;
}) => {
  const { t } = useTranslation(["organization"]);
  const navigate = useNavigate();
  const setOrgMutation = useSetOrgId();

  const { name, profile_type, org_type: channel } = orgData;

  const amazonProfileName = "amazon_profile_name" in orgData ? orgData.amazon_profile_name : orgData.profile_name;
  const orgId = "org_id" in orgData ? orgData.org_id : orgData.id;
  const orgIsVisible = "org_is_visible" in orgData ? orgData.org_is_visible : orgData.is_visible;
  const tags = "tags" in orgData ? orgData.tags : null;
  const tagsEnhanced = "tags_enhanced" in orgData ? orgData.tags_enhanced : undefined;

  let flagNationCode = marketPlace;

  if (flagNationCode === "USA") {
    flagNationCode = "US";
  }

  if (flagNationCode === "UK") {
    flagNationCode = "GB";
  }
  // Dynamic lookup by country code — there is no static member to reference.
  // eslint-disable-next-line import/namespace
  const Flag = Flags[flagNationCode as keyof typeof Flags];

  const profileTypeLabel = (() => {
    switch (profile_type) {
      case "seller":
        return t(($) => $.organization.profileType["seller"]);
      case "vendor":
        return t(($) => $.organization.profileType["vendor"]);
      default:
        return snakeCaseToSentenceCase(profile_type);
    }
  })();

  return (
    <TrellisLink
      id="categoryPage"
      to={"#"}
      onClick={() => {
        if (changeOrgOnClick && marketPlace) {
          setOrgMutation.mutate(orgId, {
            onSuccess() {
              localStorage.setItem("Marketplace", marketPlace);
              localStorage.setItem("channel", channel.toLowerCase());
              /*
            TODO: Below is bad, but fixing how we store channel and marketplace in state will open a can of worms
            The above dispatches and localStorage updates make everything functional with the right marketplace filters,
            but the seller marketplace and channel dropdowns will need an update to trigger a refetch without any type
            of hard reload once localStorage is updated
            */
              void navigate({ to: "/user/dashboard", reloadDocument: true });
            },
          });
        }
      }}
      style={{
        textDecoration: "none",
        fontWeight: "500",
        fontSize: "1.5rem",
        cursor: changeOrgOnClick ? "pointer" : "inherit",
      }}
    >
      <div className={"fs-standard text-underline-hover"}>
        <strong className={"text-underline-hover"}>{name}</strong>
        {marketPlace && <Flag style={{ width: "18px" }} className="ms-3" />}{" "}
      </div>

      <i
        className="pe-3"
        style={{
          fontWeight: "light",
          color: "#666",
          fontSize: "1rem",
        }}
      >
        {amazonProfileName}
      </i>

      <span style={{ marginLeft: "2px", marginRight: "2px" }}>{renderChannelIcon(channel)}</span>
      {!orgIsVisible && (
        <span
          style={{
            color: "#666",
            fontSize: "1rem",
            marginLeft: "1rem",
            fontWeight: "light",
            fontStyle: "italic",
          }}
        >
          {t(($) => $.organization["Hidden"])}
        </span>
      )}
      <br />
      <Badge
        className={`badge bg-trellis-${profile_type === "seller" ? "fuchsia" : "mauve"} fs-6 ${css({
          borderRadius: "5px",
          marginRight: "small",
        })}`}
        pill
      >
        {profileTypeLabel}
      </Badge>
      <OverflowTags enhancedTags={tagsEnhanced} tags={tags ?? undefined} id={orgId} />
    </TrellisLink>
  );
};
